import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';
import { TestimonialAvatar } from '../TestimonialAvatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch(`${API}/testimonials`);
        const data = await response.json();
        // Sort by date (most recent first)
        const sorted = data.sort((a, b) => {
          const dateA = new Date(a.date || a.created_at || 0);
          const dateB = new Date(b.date || b.created_at || 0);
          return dateB - dateA;
        });
        setTestimonials(sorted);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  const next = useCallback(() => {
    if (testimonials.length > 0) {
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }
  }, [testimonials.length]);

  const prev = useCallback(() => {
    if (testimonials.length > 0) {
      setCurrent((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    }
  }, [testimonials.length]);

  useEffect(() => {
    if (testimonials.length > 1) {
      const timer = setInterval(next, 5000);
      return () => clearInterval(timer);
    }
  }, [next, testimonials.length]);

  if (loading || testimonials.length === 0) {
    return (
      <section className="py-20 md:py-32 bg-muted">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="h-64 bg-card animate-pulse rounded-lg" />
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 md:py-32 bg-muted" data-testid="testimonials-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-sm uppercase tracking-widest text-muted-foreground block mb-4">
            Testimonios
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight">
            Lo Que Dicen Mis Clientes
          </h2>
        </motion.div>

        {/* Testimonial Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
              className="text-center px-8 md:px-16"
            >
              {/* Quote Icon */}
              <Quote className="w-12 h-12 mx-auto mb-8 text-muted-foreground/30" />
              
              {/* Content */}
              <p className="font-serif text-xl md:text-2xl lg:text-3xl leading-relaxed mb-8">
                "{testimonials[current].content}"
              </p>

              {/* Rating */}
              <div className="flex justify-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < testimonials[current].rating 
                        ? 'text-amber-500 fill-current' 
                        : 'text-muted-foreground'
                    }`}
                  />
                ))}
              </div>

              {/* Client Info */}
              <div className="flex items-center justify-center gap-4">
                <TestimonialAvatar src={testimonials[current].client_image} name={testimonials[current].client_name} />
                <div className="text-left">
                  <h4 className="font-medium">{testimonials[current].client_name}</h4>
                  <p className="text-sm text-muted-foreground">{testimonials[current].service_type}</p>
                  {(testimonials[current].date || testimonials[current].created_at) && (
                    <p className="text-xs text-muted-foreground/70" data-testid="testimonial-date">
                      {new Date(testimonials[current].date || testimonials[current].created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'long' })}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-center gap-4 mt-12">
            <button
              onClick={prev}
              className="w-12 h-12 border border-border rounded-full flex items-center justify-center hover:bg-card transition-colors"
              data-testid="testimonial-prev"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              className="w-12 h-12 border border-border rounded-full flex items-center justify-center hover:bg-card transition-colors"
              data-testid="testimonial-next"
              aria-label="Siguiente"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrent(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === current ? 'bg-foreground w-6' : 'bg-muted-foreground/30'
                }`}
                data-testid={`testimonial-dot-${index}`}
                aria-label={`Ir a testimonio ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
